// src/features/tidsregistering/pages/MineDagePage.jsx
import { useTranslation } from 'react-i18next';
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, IconButton, Tooltip, CircularProgress,
  Alert, ToggleButton, ToggleButtonGroup, Dialog, DialogTitle, DialogContent,
  DialogActions, Button, Divider,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as UncheckedIcon,
  Visibility as ViewIcon,
  Undo as UndoIcon,
} from '@mui/icons-material';
import { format, startOfMonth } from 'date-fns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';

import { useDayOverviews } from '../hooks/useDayOverviews';
import { useSnackbar } from '../../../hooks/mui/useSnackBar';
import { useAuth } from '../../../context/AuthContext';
import { useTidsregistreringDayOverviewPermissions } from '../../auth/hooks/usePermissions';
import { SEVERITY_CHIP, SEVERITY_ALERT, DEVIATION_LABELS } from '../constants/severity';

import PageHeader from '../../../components/common/PageHeader';
import TodayIcon from '@mui/icons-material/Today';

function DayDetailDialog({ open, overview, onClose, onApprove, onRevoke, approveLoading, canApprove }) {
  if (!overview) return null;

  const diff = Number(overview.hours_diff_from_scheduled);
  const diffLabel = diff === 0 ? '–' : `${diff > 0 ? '+' : ''}${diff.toFixed(2)}t`;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Typography variant="h6">Dag-detaljer</Typography>
        <Typography variant="body2" color="text.secondary">
          {format(new Date(overview.date), 'EEEE d. MMMM yyyy')}
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        {/* Summary stats */}
        <Box sx={{ display: 'flex', gap: 3, mb: 2, flexWrap: 'wrap' }}>
          <Box>
            <Typography variant="caption" color="text.secondary">Timer arbejdet</Typography>
            <Typography variant="h6">{Number(overview.hours_sum).toFixed(2)}t</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Afvigelse fra plan</Typography>
            <Typography variant="h6" color={diff > 0 ? 'warning.main' : diff < 0 ? 'error.main' : 'text.primary'}>
              {diffLabel}
            </Typography>
          </Box>
          {overview.first_registration && (
            <Box>
              <Typography variant="caption" color="text.secondary">Check ind</Typography>
              <Typography variant="h6">{String(overview.first_registration).slice(0, 5)}</Typography>
            </Box>
          )}
          {overview.last_registration && (
            <Box>
              <Typography variant="caption" color="text.secondary">Check ud</Typography>
              <Typography variant="h6">{String(overview.last_registration).slice(0, 5)}</Typography>
            </Box>
          )}
        </Box>

        {/* Time details */}
        {overview.time_details?.filter((td) => td.is_latest_version).length > 0 && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" gutterBottom>Tidsdetaljer</Typography>
            <Table size="small" sx={{ mb: 1 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Start</TableCell>
                  <TableCell>Slut</TableCell>
                  <TableCell align="right">Timer</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {overview.time_details
                  .filter((td) => td.is_latest_version)
                  .sort((a, b) => String(a.start_time).localeCompare(String(b.start_time)))
                  .map((td) => (
                    <TableRow key={td.time_detail_id}>
                      <TableCell>{String(td.start_time).slice(0, 5)}</TableCell>
                      <TableCell>{String(td.end_time).slice(0, 5)}</TableCell>
                      <TableCell align="right">{Number(td.hours_sum).toFixed(2)}t</TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </>
        )}

        {/* Deviations */}
        {overview.deviations?.length > 0 && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" gutterBottom>Afvigelser</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {overview.deviations.map((dev) => (
                <Alert key={dev.deviation_id} severity={SEVERITY_ALERT[dev.severity_code] ?? 'info'} sx={{ py: 0 }}>
                  <Typography variant="body2">
                    <strong>{DEVIATION_LABELS[dev.type] ?? dev.type}:</strong> {dev.message}
                  </Typography>
                </Alert>
              ))}
            </Box>
          </>
        )}

        <Divider sx={{ my: 2 }} />

        {/* Approval status */}
        <Typography variant="subtitle2" gutterBottom>Godkendelsesstatus</Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {overview.approved_by_user
              ? <CheckCircleIcon fontSize="small" color="success" />
              : <UncheckedIcon fontSize="small" color="disabled" />}
            <Typography variant="body2">
              Min godkendelse
              {overview.approved_by_user && overview.user_approval_changed && (
                <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                  {format(new Date(overview.user_approval_changed), 'dd/MM/yyyy HH:mm')}
                </Typography>
              )}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {overview.approved_by_admin
              ? <CheckCircleIcon fontSize="small" color="success" />
              : <UncheckedIcon fontSize="small" color="disabled" />}
            <Typography variant="body2">Admin godkendelse</Typography>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Luk</Button>
        {!canApprove ? null : overview.approved_by_user ? (
          <Button
            variant="outlined"
            color="warning"
            startIcon={approveLoading ? <CircularProgress size={16} color="inherit" /> : <UndoIcon />}
            disabled={approveLoading}
            onClick={() => onRevoke(overview.day_overview_id)}
          >
            Tilbagekald godkendelse
          </Button>
        ) : (
          <Button
            variant="contained"
            color="success"
            startIcon={approveLoading ? <CircularProgress size={16} color="inherit" /> : <CheckCircleIcon />}
            disabled={approveLoading}
            onClick={() => onApprove(overview.day_overview_id)}
          >
            Godkend dag
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default function MineDagePage() {
  const { t } = useTranslation(['tidsregistering', 'common']);
  const { user } = useAuth();
  const { showSuccess, showError } = useSnackbar();
  const { dayOverviews, fetchForStaff, approveByUser, revokeUserApproval, loading, error } = useDayOverviews();
  // Approving one's own days (and taking it back) takes day_overview:approve
  const { canApprove } = useTidsregistreringDayOverviewPermissions();

  const [startDate, setStartDate] = useState(startOfMonth(new Date()));
  const [endDate, setEndDate] = useState(new Date());
  const [filter, setFilter] = useState('all');
  const [selectedOverview, setSelectedOverview] = useState(null);
  const [approveLoading, setApproveLoading] = useState(false);

  const load = useCallback(() => {
    if (!user?.staff_member_id) return;
    fetchForStaff(
      user.staff_member_id,
      format(startDate, 'yyyy-MM-dd'),
      format(endDate, 'yyyy-MM-dd'),
    );
  }, [fetchForStaff, user?.staff_member_id, startDate, endDate]);

  useEffect(() => { load(); }, [load]);

  const filtered = dayOverviews
    .filter((o) => {
      if (filter === 'pending') return !o.approved_by_user;
      if (filter === 'approved') return o.approved_by_user;
      return true;
    })
    .sort((a, b) => new Date(b.date) - new Date(a.date));

  const handleApprove = useCallback(async (dayOverviewId) => {
    setApproveLoading(true);
    const result = await approveByUser(dayOverviewId);
    setApproveLoading(false);
    if (result.success) {
      showSuccess('Dag godkendt');
      setSelectedOverview((prev) => prev?.day_overview_id === dayOverviewId ? result.data : prev);
      load();
    } else {
      showError(result.error ?? 'Kunne ikke godkende');
    }
  }, [approveByUser, showSuccess, showError, load]);

  const handleRevoke = useCallback(async (dayOverviewId) => {
    setApproveLoading(true);
    const result = await revokeUserApproval(dayOverviewId);
    setApproveLoading(false);
    if (result.success) {
      showSuccess('Godkendelse tilbagekaldt');
      setSelectedOverview((prev) => prev?.day_overview_id === dayOverviewId ? result.data : prev);
      load();
    } else {
      showError(result.error ?? 'Kunne ikke tilbagekalde');
    }
  }, [revokeUserApproval, showSuccess, showError, load]);

  const pendingCount = dayOverviews.filter((o) => !o.approved_by_user).length;

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', gap: 2 }}>
      <PageHeader
        mb={4}
        icon={TodayIcon}
        title={t('common:navigation.tidsregistering_mine_days')}
        subtitle={t('tidsregistering:page_subtitles.mine_days')}
        badges={pendingCount > 0 && (
          <Chip label={`${pendingCount} afventer`} color="warning" size="small" />
        )}
      />

      {/* Controls */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <>
          <DatePicker
            label="Fra"
            value={startDate}
            onChange={(d) => d && setStartDate(d)}
            slotProps={{ textField: { size: 'small' } }}
          />
          <DatePicker
            label="Til"
            value={endDate}
            onChange={(d) => d && setEndDate(d)}
            slotProps={{ textField: { size: 'small' } }}
          />
        </>

        <ToggleButtonGroup
          size="small"
          value={filter}
          exclusive
          onChange={(_, v) => v && setFilter(v)}
        >
          <ToggleButton value="all">Alle</ToggleButton>
          <ToggleButton value="pending">Afventer</ToggleButton>
          <ToggleButton value="approved">Godkendt</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {error && <Alert severity="error">{error}</Alert>}

      {/* Table */}
      <TableContainer component={Paper} variant="outlined" sx={{ flex: 1, overflow: 'auto' }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>Dato</TableCell>
              <TableCell align="right">Timer</TableCell>
              <TableCell align="right">Afvigelse</TableCell>
              <TableCell align="center">Alvorlighed</TableCell>
              <TableCell align="center">Min godkendelse</TableCell>
              <TableCell align="center">Admin</TableCell>
              <TableCell align="right">Handlinger</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && dayOverviews.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : filtered.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.disabled' }}>
                  Ingen dag-oversigter fundet
                </TableCell>
              </TableRow>
            ) : filtered.map((overview) => {
              const sev = SEVERITY_CHIP[overview.severity_code] ?? SEVERITY_CHIP[0];
              const diff = Number(overview.hours_diff_from_scheduled);
              const diffLabel = diff === 0 ? '–' : `${diff > 0 ? '+' : ''}${diff.toFixed(2)}t`;

              return (
                <TableRow
                  key={overview.day_overview_id}
                  hover
                  sx={{ opacity: overview.approved_by_user ? 0.65 : 1 }}
                >
                  <TableCell>
                    <Typography variant="body2">
                      {format(new Date(overview.date), 'EEEE dd/MM/yyyy')}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2">{Number(overview.hours_sum).toFixed(2)}t</Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography
                      variant="body2"
                      color={diff > 0 ? 'warning.main' : diff < 0 ? 'error.main' : 'text.secondary'}
                    >
                      {diffLabel}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Chip label={sev.label} color={sev.color} size="small" variant="outlined" />
                  </TableCell>
                  <TableCell align="center">
                    {overview.approved_by_user
                      ? <CheckCircleIcon fontSize="small" color="success" />
                      : <UncheckedIcon fontSize="small" color="disabled" />}
                  </TableCell>
                  <TableCell align="center">
                    {overview.approved_by_admin
                      ? <CheckCircleIcon fontSize="small" color="success" />
                      : <UncheckedIcon fontSize="small" color="disabled" />}
                  </TableCell>
                  <TableCell align="right">
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                      <Tooltip title="Vis detaljer">
                        <IconButton size="small" onClick={() => setSelectedOverview(overview)}>
                          <ViewIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {!canApprove ? null : !overview.approved_by_user ? (
                        <Tooltip title="Godkend dag">
                          <IconButton
                            size="small"
                            color="success"
                            disabled={approveLoading}
                            onClick={() => handleApprove(overview.day_overview_id)}
                          >
                            <CheckCircleIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      ) : (
                        <Tooltip title="Tilbagekald godkendelse">
                          <IconButton
                            size="small"
                            color="warning"
                            disabled={approveLoading}
                            onClick={() => handleRevoke(overview.day_overview_id)}
                          >
                            <UndoIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <DayDetailDialog
        open={!!selectedOverview}
        overview={selectedOverview}
        onClose={() => setSelectedOverview(null)}
        onApprove={handleApprove}
        onRevoke={handleRevoke}
        approveLoading={approveLoading}
        canApprove={canApprove}
      />
    </Box>
  );
}
